import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status string any of the 6 Home Affairs modules' APIs
// actually return (see src/lib/types.ts for the full per-module enum list).
// Unmapped statuses still render, just in the neutral slate tone.
const STATUS_TONES: Record<string, string> = {
  // ── Shared baseline (fleet convention) ──────────────────────────────────
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GRANTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DONE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CLEAR: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CERT_ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PERMIT_ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REGISTERED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COLLECTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ALIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PASS: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DENIED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  HIT: 'bg-rose-50 text-rose-700 ring-rose-200',
  REFUSED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DEFECTS: 'bg-rose-50 text-rose-700 ring-rose-200',
  ESCAPED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DECEASED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  DISMISSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  RELEASED: 'bg-slate-100 text-slate-500 ring-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',
  MANUAL_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  OVERRIDDEN: 'bg-amber-50 text-amber-700 ring-amber-200',
  RECTIFICATION: 'bg-amber-50 text-amber-700 ring-amber-200',
  UNDER_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  ISSUED_WITH_DISCLOSURE: 'bg-amber-50 text-amber-700 ring-amber-200',
  NO_SHOW: 'bg-amber-50 text-amber-700 ring-amber-200',
  DAMAGED: 'bg-amber-50 text-amber-700 ring-amber-200',
  OUT_OF_SERVICE: 'bg-amber-50 text-amber-700 ring-amber-200',

  // ── M01 population — BirthState / CertificateOrderStatus / PersonStatus ──
  NOTIFIED: 'bg-slate-100 text-slate-600 ring-slate-200',
  PARENT_COMPLETED: 'bg-sky-50 text-sky-700 ring-sky-200',
  REGISTRAR_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  PENDING_PAYMENT: 'bg-sky-50 text-sky-700 ring-sky-200',

  // ── M03 residency — ApplicationState / PermitStatus ─────────────────────
  DOCS_VERIFIED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  OFFICER_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  FEE_PAID: 'bg-violet-50 text-violet-700 ring-violet-200',

  // ── M04 passports — PassportApplicationState ────────────────────────────
  APPLIED: 'bg-slate-100 text-slate-600 ring-slate-200',
  BIOMETRICS: 'bg-sky-50 text-sky-700 ring-sky-200',
  VETTING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  PERSONALISATION: 'bg-violet-50 text-violet-700 ring-violet-200',
  QC: 'bg-violet-50 text-violet-700 ring-violet-200',
  READY_FOR_COLLECTION: 'bg-teal-50 text-teal-700 ring-teal-200',
  READY_FOR_DISPATCH: 'bg-teal-50 text-teal-700 ring-teal-200',
  DISPATCHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // ── M05 police — IncidentState ──────────────────────────────────────────
  TRIAGED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ASSIGNED: 'bg-violet-50 text-violet-700 ring-violet-200',
  INVESTIGATING: 'bg-violet-50 text-violet-700 ring-violet-200',

  // ── M06 fire-rescue — FireCertState / InspectionStatus ──────────────────
  DOC_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  INSPECTION_SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-200',
  INSPECTED: 'bg-violet-50 text-violet-700 ring-violet-200',
  BOOKED: 'bg-sky-50 text-sky-700 ring-sky-200',
  RECEIVED: 'bg-sky-50 text-sky-700 ring-sky-200',
  INSPECTION_BOOKED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',

  // ── M07 prisons — InmateStatus / VisitStatus ────────────────────────────
  ADMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_CUSTODY: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PENDING_VETTING: 'bg-sky-50 text-sky-700 ring-sky-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/** Inline notice shown when the signed-in persona lacks a role a screen's mutating actions require — reads are usually still visible, writes will 403 for real. */
export function RoleNotice({ roles }: { roles: string[] }) {
  return (
    <div className="mb-4">
      <ErrorNote
        message={`Your persona does not hold ${roles.join(' or ')} — actions on this screen will be rejected by the API (403). You can still view what's below.`}
      />
    </div>
  );
}

/** Label/value pair used throughout every workspace's detail modals. */
export function Detail({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

/** Compact tab strip used to switch between an active queue and the full list within one workspace screen. */
export function TabStrip<T extends string>({ tabs, active, onChange }: { tabs: Array<{ key: T; label: string }>; active: T; onChange: (key: T) => void }) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            active === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
